import { UIRouterGlobals } from "@uirouter/angular";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { ReflectionNoteType, ReflectionOverviewFilterName, ReflectionOverviewFilterType, reflectionNoteTypes } from "@webapp/reflections/models/reflections.models";

@UntilDestroy()
@Component({
  selector: "reflection-overview",
  templateUrl: "./reflection-overview.component.html",
  styleUrls: ["./reflection-overview.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionOverviewComponent implements OnChanges, OnInit {
  /**
   * @param filters - filters to show in the Overview
   */
  @Input()
  public filters: ReflectionOverviewFilterType;

  /**
   * @param isNotesSettingEnabled - notes setting located in manage form
   */
  @Input()
  public isNotesSettingEnabled: boolean;

  /**
   * @param isCompareModeEnabled - compare mode used to control responsive design of filters
   */
  @Input()
  public isCompareModeEnabled: boolean;

  /**
   * @param onSelectedFilterChange - emits updated filters on selecting different filter
   */
  @Output()
  public readonly selectedFilterChange = new EventEmitter<ReflectionOverviewFilterType>();

  public isAnyFilterSelected: boolean;
  public areAnyNotesAvailable: boolean;
  public isCheckInsUxImprovementsEnabled: boolean;
  public isLeftNavigationEnabled: boolean;

  constructor(
    featureToggleFacade: FeatureTogglesFacade,
    isNavExpanded: IsNavigationExpandedCache,
    private cdr: ChangeDetectorRef,
    private routerGlobals: UIRouterGlobals
  ) {
    featureToggleFacade
      .isFeatureAvailable$(FeatureFlag.CheckInsUxImprovements)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (enabled) => {
          this.isCheckInsUxImprovementsEnabled = enabled;
        },
      });

    isNavExpanded.get$().subscribe((value) => {
      this.isLeftNavigationEnabled = value;
      this.cdr.markForCheck();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.isAnyFilterSelected = Object.keys(this.filters).some((key: ReflectionOverviewFilterName) => !!this.filters[key].count && this.filters[key].selected);

    this.areAnyNotesAvailable = reflectionNoteTypes.some((key: ReflectionNoteType) => this.filters[key]?.count);

    if (changes.isCompareModeEnabled) {
      this.cdr.markForCheck();
    }
  }

  public ngOnInit(): void {
    if (this.routerGlobals.params["note"]) {
      this.updateFilter(this.routerGlobals.params["note"]);
    }
  }

  public updateFilter(selectedFilter?: ReflectionOverviewFilterName): void {
    const filtersClone: ReflectionOverviewFilterType = structuredClone(this.filters);

    this.deselectAllFilters(filtersClone);
    if (selectedFilter) {
      filtersClone[selectedFilter].selected = !this.filters[selectedFilter].selected;
    }

    this.selectedFilterChange.emit(filtersClone);
  }

  private deselectAllFilters(filters): void {
    Object.keys(filters).forEach((key: ReflectionOverviewFilterName) => {
      filters[key].selected = false;
    });
  }
}
